import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  ChurchActivityListResult,
  ActivityEntryFormGroup,
} from '@features/report/activity-entry/types/activityEntry';
import {
  fetchActivityEntryFormAsync,
  ActivityEntryFormFetchParams,
} from '../services/activityEntrySvc';
import type { ActivityEntryScope } from './useActivityEntryAction';

export type ScopedActivityEntryParams = {
  scope?: ActivityEntryScope;
  entityId: number;
  reportTemplateId: number;
  reportTagId: number;
  page?: number;
  perPage?: number;
};

export function useActivityEntry(
  params: ScopedActivityEntryParams
): ChurchActivityListResult {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ActivityEntryFormGroup[]>([]);
  const [requestFailed, setRequestFailed] = useState(false);
  const requestGenerationRef = useRef(0);

  const scope = params.scope;
  const entityId = params.entityId;
  const reportTemplateId = params.reportTemplateId;
  const reportTagId = params.reportTagId;
  const page = params.page;
  const perPage = params.perPage;

  const enabled =
    Boolean(scope) && entityId > 0 && reportTemplateId > 0 && reportTagId > 0;

  const fetchParams: ActivityEntryFormFetchParams | null = useMemo(() => {
    if (!scope) {
      return null;
    }
    return { scope, entityId, reportTemplateId, reportTagId, page, perPage };
  }, [scope, entityId, reportTemplateId, reportTagId, page, perPage]);

  const reload = useCallback(async (): Promise<ActivityEntryFormGroup[] | null> => {
    if (!enabled || !fetchParams) {
      // Idle: never flip loading true→false. Only clear when not already idle.
      setLoading((prev) => (prev ? false : prev));
      setData((prev) => (prev.length ? [] : prev));
      setRequestFailed((prev) => (prev ? false : prev));
      return null;
    }

    const generation = ++requestGenerationRef.current;
    setLoading(true);

    try {
      const response = await fetchActivityEntryFormAsync(fetchParams);

      if (generation !== requestGenerationRef.current) {
        return null;
      }

      if (response.isSuccess()) {
        const payload = response.getData();
        const items = Array.isArray(payload) ? payload : payload?.data ?? [];
        setData(items);
        setRequestFailed(false);
        return items;
      }

      setData([]);
      setRequestFailed(true);
      return null;
    } catch {
      if (generation !== requestGenerationRef.current) {
        return null;
      }
      setData([]);
      setRequestFailed(true);
      return null;
    } finally {
      if (generation === requestGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, fetchParams]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, requestFailed, reload };
}
